/**
 * Hooks Install — Manage Claude Code settings.local.json hook entries
 *
 * Manages 10 MutagenT hooks (v2):
 *   SessionStart, Stop, PreToolUse, PostToolUse,
 *   UserPromptSubmit, SubagentStart, SubagentStop,
 *   PreCompact, PostCompact, PostToolUseFailure
 */

import { readFileSync, writeFileSync, existsSync, mkdirSync } from 'fs';
import { join } from 'path';

// ============================================================================
// ParseError — thrown when settings.local.json contains invalid JSON
// ============================================================================

export class SettingsParseError extends Error {
  constructor(
    public readonly settingsPath: string,
    public readonly backupPath: string,
    cause?: unknown,
  ) {
    super(
      `settings.local.json contains unparseable JSON.\n` +
      `  Original file backed up to: ${backupPath}\n` +
      `  To recover: restore from backup, fix JSON syntax, then re-run ` +
      `\`mutagent hooks install\``,
    );
    this.name = 'SettingsParseError';
    if (cause instanceof Error) this.cause = cause;
  }
}

// ============================================================================
// Types
// ============================================================================

/** Shape of a single hook entry in .claude/settings.local.json */
export interface ClaudeHookEntry {
  type: string;
  command: string;
}

export interface ClaudeHookMatcher {
  matcher?: string;
  hooks: ClaudeHookEntry[];
}

export interface ClaudeSettingsHooks {
  SessionStart?: ClaudeHookMatcher[];
  Stop?: ClaudeHookMatcher[];
  SessionEnd?: ClaudeHookMatcher[];
  PreToolUse?: ClaudeHookMatcher[];
  PostToolUse?: ClaudeHookMatcher[];
  UserPromptSubmit?: ClaudeHookMatcher[];
  SubagentStart?: ClaudeHookMatcher[];
  SubagentStop?: ClaudeHookMatcher[];
  PreCompact?: ClaudeHookMatcher[];
  PostCompact?: ClaudeHookMatcher[];
  PostToolUseFailure?: ClaudeHookMatcher[];
  [key: string]: ClaudeHookMatcher[] | undefined;
}

export interface ClaudeSettings {
  hooks?: ClaudeSettingsHooks;
  [key: string]: unknown;
}

// ============================================================================
// v1 → v2 migration table
// ============================================================================

/**
 * Known v1 hook mappings that are WRONG in v2 and must be removed on install.
 * Key = Claude Code event name. Value = commands to remove from that event's matchers.
 *
 * v1 put session-end under Stop (which closes the turn in Claude Code's semantics).
 * v2 moves session-end to its own SessionEnd event (correct trace-closer).
 */
const V1_MIGRATIONS: Record<string, string[]> = {
  Stop: ['mutagent hooks claude-code session-end'],
};

// ============================================================================
// Hook definitions (all 10 MutagenT hooks)
// ============================================================================

/** All 11 MutagenT hook entries we manage (v2).
 *
 * IMPORTANT — Stop vs SessionEnd:
 *   Claude Code fires `Stop` when Claude finishes responding to a user turn.
 *   This MUST close the turn span only, NOT the trace.
 *   `SessionEnd` is a separate Claude Code event that fires when the session
 *   actually terminates — that's what closes the trace.
 */
const MUTAGENT_HOOKS: ClaudeSettingsHooks = {
  // Original hooks
  SessionStart: [{ matcher: 'startup', hooks: [{ type: 'command', command: 'mutagent hooks claude-code session-start' }] }],
  PreToolUse: [{ hooks: [{ type: 'command', command: 'mutagent hooks claude-code pre-tool-use' }] }],
  PostToolUse: [{ hooks: [{ type: 'command', command: 'mutagent hooks claude-code post-tool-use' }] }],
  // Stop closes the TURN span (per-message), not the trace
  Stop: [{ hooks: [{ type: 'command', command: 'mutagent hooks claude-code stop' }] }],
  // SessionEnd closes the entire trace (session terminates)
  SessionEnd: [{ hooks: [{ type: 'command', command: 'mutagent hooks claude-code session-end' }] }],
  // v2 hooks
  UserPromptSubmit: [{ hooks: [{ type: 'command', command: 'mutagent hooks claude-code user-prompt-submit' }] }],
  SubagentStart: [{ hooks: [{ type: 'command', command: 'mutagent hooks claude-code subagent-start' }] }],
  SubagentStop: [{ hooks: [{ type: 'command', command: 'mutagent hooks claude-code subagent-stop' }] }],
  PreCompact: [{ hooks: [{ type: 'command', command: 'mutagent hooks claude-code pre-compact' }] }],
  PostCompact: [{ hooks: [{ type: 'command', command: 'mutagent hooks claude-code post-compact' }] }],
  PostToolUseFailure: [{ hooks: [{ type: 'command', command: 'mutagent hooks claude-code post-tool-use-failure' }] }],
};

// ============================================================================
// Install helpers
// ============================================================================

/**
 * Check whether a MutagenT command is already present in a matchers array.
 * Deduplication is done by command string.
 */
export function hasCommand(matchers: ClaudeHookMatcher[], command: string): boolean {
  return matchers.some(m => m.hooks.some(h => h.command === command));
}

/**
 * Deep-merge MutagenT hooks into an existing settings object.
 * Adds only entries not already present (checked by command string).
 * Mutates and returns the settings object.
 */
export function mergeHooksIntoSettings(settings: ClaudeSettings): ClaudeSettings {
  settings.hooks ??= {};

  for (const [event, newMatchers] of Object.entries(MUTAGENT_HOOKS)) {
    if (!newMatchers) continue;

    settings.hooks[event] ??= [];
    const existing = settings.hooks[event];

    for (const matcher of newMatchers) {
      for (const hook of matcher.hooks) {
        if (!hasCommand(existing, hook.command)) {
          const newMatcher: ClaudeHookMatcher = {
            ...(matcher.matcher !== undefined ? { matcher: matcher.matcher } : {}),
            hooks: [hook],
          };
          existing.push(newMatcher);
        }
      }
    }
  }

  return settings;
}

/**
 * Remove known-wrong v1 hook mappings from a settings object.
 * Scans V1_MIGRATIONS and strips matching commands from their event matchers.
 * Preserves all other hooks in the same event (including custom user hooks).
 * Returns a list of what was removed so callers can warn the user.
 */
export function migrateV1Hooks(settings: ClaudeSettings): {event: string; command: string}[] {
  const migrated: {event: string; command: string}[] = [];
  if (!settings.hooks) return migrated;

  for (const [event, commandsToRemove] of Object.entries(V1_MIGRATIONS)) {
    const matchers = settings.hooks[event];
    if (!matchers) continue;

    const filtered = matchers
      .map((matcher) => {
        const keptHooks = matcher.hooks.filter((h) => {
          if (commandsToRemove.includes(h.command)) {
            migrated.push({ event, command: h.command });
            return false;
          }
          return true;
        });
        return keptHooks.length > 0 ? { ...matcher, hooks: keptHooks } : null;
      })
      .filter(Boolean) as ClaudeHookMatcher[];

    if (filtered.length === 0) {
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete settings.hooks[event];
    } else {
      settings.hooks[event] = filtered;
    }
  }
  return migrated;
}

/**
 * Install MutagenT hooks into .claude/settings.local.json (cwd-relative).
 * Returns a summary of what happened.
 *
 * Throws `SettingsParseError` if the existing settings file contains invalid
 * JSON. In that case the original file is backed up to
 * `<settingsPath>.bak.<ISO-timestamp>` BEFORE any write, so the operator can
 * recover. The error message names the backup path and suggests a fix.
 *
 * When the installer actually modifies the file (adds hooks), a `userWarning`
 * string is returned. Callers should surface this to the user:
 *   - human mode: write to stderr
 *   - --json mode: include as `warnings: [string]` in the response
 */
export function installHooks(cwd: string): {
  settingsPath: string;
  existed: boolean;
  added: string[];
  alreadyPresent: string[];
  migrated: {event: string; command: string}[];
  userWarning?: string;
} {
  const claudeDir = join(cwd, '.claude');
  const settingsPath = join(claudeDir, 'settings.local.json');
  const existed = existsSync(settingsPath);

  let settings: ClaudeSettings = {};
  if (existed) {
    const raw = readFileSync(settingsPath, 'utf-8');
    try {
      settings = JSON.parse(raw) as ClaudeSettings;
    } catch (err) {
      // Back up the unparseable file before aborting
      const backupPath = `${settingsPath}.bak.${new Date().toISOString().replace(/:/g, '-')}`;
      writeFileSync(backupPath, raw, 'utf-8');
      throw new SettingsParseError(settingsPath, backupPath, err);
    }
  }

  const added: string[] = [];
  const alreadyPresent: string[] = [];

  // Migrate v1 hooks before merging v2
  const migrated = migrateV1Hooks(settings);

  for (const [event, newMatchers] of Object.entries(MUTAGENT_HOOKS)) {
    if (!newMatchers) continue;
    settings.hooks ??= {};
    settings.hooks[event] ??= [];
    const existing = settings.hooks[event];

    for (const matcher of newMatchers) {
      for (const hook of matcher.hooks) {
        if (hasCommand(existing, hook.command)) {
          alreadyPresent.push(hook.command);
        } else {
          const newMatcher: ClaudeHookMatcher = {
            ...(matcher.matcher !== undefined ? { matcher: matcher.matcher } : {}),
            hooks: [hook],
          };
          existing.push(newMatcher);
          added.push(hook.command);
        }
      }
    }
  }

  let userWarning: string | undefined;

  if (added.length > 0 || migrated.length > 0) {
    if (!existsSync(claudeDir)) {
      mkdirSync(claudeDir, { recursive: true });
    }
    writeFileSync(settingsPath, JSON.stringify(settings, null, 2) + '\n', 'utf-8');
  }

  // Surface a user-visible warning when the file was actually modified (not a no-op run)
  if (added.length > 0) {
    const addedList = added.map(cmd => {
      // Extract the subcommand suffix (e.g. 'session-start') for a shorter display
      const parts = cmd.split(' ');
      return parts[parts.length - 1] ?? cmd;
    }).join(', ');
    userWarning =
      `MutagenT hooks installed into .claude/settings.local.json\n` +
      `  Added: ${addedList}\n` +
      `  This file was modified. Review with: git diff .claude/settings.local.json\n` +
      `  (To remove hooks, edit .claude/settings.local.json and delete the mutagent entries)`;
  }

  return { settingsPath, existed, added, alreadyPresent, migrated, userWarning };
}
